import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { DownloadSimple, ChartBar, UsersThree } from "@phosphor-icons/react";

export default function Reports() {
  const { storeId } = useParams();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);
  const [rows, setRows] = useState([]);
  const [sales, setSales] = useState([]);
  const [execRows, setExecRows] = useState([]);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get(`/store/${storeId}/reports/by-product?start=${start}&end=${end}`),
      api.get(`/store/${storeId}/sales?start=${start}&end=${end}T23:59:59`),
      api.get(`/store/${storeId}/reports/by-executive?start=${start}&end=${end}`),
    ]);
    setRows(a.data);
    setSales(b.data);
    setExecRows(c.data);
  };
  useEffect(() => { load(); }, [storeId]);

  const exportCsv = async () => {
    const token = localStorage.getItem("pos_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/store/${storeId}/reports/export.csv?start=${start}&end=${end}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sales_${storeId}_${start}_${end}.csv`;
    a.click();
  };

  const live = sales.filter((s) => !s.cancelled);
  const totalRevenue = live.reduce((a, s) => a + (s.grand_total || 0), 0);
  const totalBills = live.length;
  const avgBill = totalBills ? totalRevenue / totalBills : 0;

  return (
    <Shell>
      <PageHeader
        title="Sales Reports"
        subtitle="Analyze sales by date range, export CSV."
        testId="reports-header"
        actions={<button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 h-10 px-4 bg-slate-900 text-white text-sm font-semibold rounded-md hover:bg-slate-800"><DownloadSimple size={16} weight="bold" />Export CSV</button>}
      />
      <div className="p-8 space-y-4">
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Start</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="start-date" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">End</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="end-date" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          </div>
          <button onClick={load} data-testid="apply-range" className="h-10 px-5 bg-slate-900 text-white font-semibold rounded-md text-sm">Apply</button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Revenue</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2">{inr(totalRevenue)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Bills</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2">{totalBills}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Avg Bill</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2">{inr(avgBill)}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <UsersThree size={16} />
            <h2 className="font-display font-bold">Executive Performance</h2>
          </div>
          <table className="w-full text-sm" data-testid="executive-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3">Executive</th>
                <th className="text-right px-4 py-3">Bills</th>
                <th className="text-right px-4 py-3">Customers Handled</th>
                <th className="text-right px-4 py-3">Items Sold</th>
                <th className="text-right px-4 py-3">Revenue</th>
                <th className="text-right px-4 py-3">Avg Bill</th>
              </tr>
            </thead>
            <tbody>
              {execRows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400" data-testid="executive-empty">No sales in this range.</td></tr>}
              {execRows.map((r) => (
                <tr key={r.executive} className="border-b border-slate-100" data-testid={`exec-perf-${r.executive}`}>
                  <td className="px-4 py-2 font-semibold">{r.executive}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{r.bills}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{r.customers}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{r.qty}</td>
                  <td className="px-4 py-2 text-right font-mono-num font-semibold">{inr(r.revenue)}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{inr(r.avg_bill)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <ChartBar size={16} />
            <h2 className="font-display font-bold">Top Products by Revenue</h2>
          </div>
          <table className="w-full text-sm" data-testid="top-products-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-right px-4 py-3">Qty Sold</th>
                <th className="text-right px-4 py-3">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-slate-400">No sales in this range.</td></tr>}
              {rows.map((r) => (
                <tr key={r.sku} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-mono-num">{r.sku}</td>
                  <td className="px-4 py-2">{r.product_name}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{r.qty}</td>
                  <td className="px-4 py-2 text-right font-mono-num font-semibold">{inr(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
